/**
 * The unread inbox, the one way. The rail's counter and the bell both read
 * this, so they can never disagree about what is new.
 *
 * @module components/inbox/useInboxUnread
 */
import { useMemo } from "react";

import { useInbox, type InboxItem } from "../../state/inbox";
import { inboxItemKey } from "./inboxPresentation";
import { useInboxReadState } from "./inboxReadState";
import { matchesInboxTriage, useInboxTriage } from "./inboxTriage";
import { useInboxScope } from "./useInboxScope";

export function useInboxUnread(): {
  readonly unread: ReadonlyArray<InboxItem>;
  readonly count: number;
  readonly available: boolean;
  readonly isPending: boolean;
} {
  const { items, available, isPending } = useInbox();
  const scope = useInboxScope();
  const triage = useInboxTriage(scope);
  const { readKeys } = useInboxReadState(scope);
  const unread = useMemo(
    () =>
      items
        .filter(
          (item) =>
            !readKeys.has(inboxItemKey(item)) &&
            matchesInboxTriage(triage.states[inboxItemKey(item)] ?? {}, "inbox", "all", triage.now),
        )
        .toSorted((left, right) => Date.parse(right.createdAt) - Date.parse(left.createdAt)),
    [items, readKeys, triage],
  );
  return { unread, count: unread.length, available, isPending };
}
